/**
 * Centralized node type configuration.
 * Single source of truth for palette, context menu, and canvas.
 * Add new node types here to make them available in the palette.
 */

import type { NodeType } from '../../types/flow-types';
import type { NodeReadiness } from './node-readiness';
import { getReadinessForPaletteKey } from './node-readiness';
import { FLOW_CARD_DEFINITIONS } from '../card-engine/registry';
import type { CardDiscoveryGroup, CardDiscoveryMetadata, CardDiscoveryRole } from '../card-engine/types';

/** Aligned with scene vocabulary: conditions, actions, utilities/special cards, logic. */
export type NodeCategory = 'conditions' | 'actions' | 'specialCards' | 'logic';
export type FlowNodeDeviceRequirement = 'presence' | 'script-capable' | 'virtual-component';

export interface NodeTypeConfig {
	type: NodeType;
	/**
	 * Unique key for this palette entry. Defaults to `type`.
	 * Use when you want multiple palette cards for the same node type (e.g. Device Condition
	 * vs Device Check are both type "condition" but have different default data).
	 */
	paletteKey?: string;
	label: string;
	/** One-liner shown in the node palette card. */
	description?: string;
	icon: string;
	color: string;
	category: NodeCategory;
	/** Per-entry factory for default node data. Falls back to buildDefaultNodeData switch. */
	defaultData?: () => Record<string, unknown>;
	/** When true the node type is not offered in the palette. */
	deprecated?: boolean;
	/** Filled by `getNodeTypesByCategory()` from `node-readiness.json`. */
	readiness?: NodeReadiness;
	premiumOnly?: boolean;
	deviceRequirement?: FlowNodeDeviceRequirement;
	/** Normalized user-intent metadata shared by all node browsers. */
	discovery: CardDiscoveryMetadata;
}

const ROLE_INTENTS: Record<CardDiscoveryRole, string[]> = {
	trigger: ['start a flow', 'react to an event'],
	condition: ['check something', 'make a decision'],
	action: ['control a device', 'do something'],
	branch: ['choose a path', 'combine logic'],
	timing: ['wait or schedule', 'control timing'],
	state: ['remember a value', 'explain the flow'],
};

const DISCOVERY_GROUP_BY_PALETTE_KEY: Record<string, CardDiscoveryGroup> = {
	action: 'devices',
	actionSequence: 'flow-logic',
	alarmAction: 'scenes',
	alarmCondition: 'scenes',
	alexaNotification: 'notifications',
	andGate: 'flow-logic',
	condition: 'devices',
	debounceTimer: 'time',
	deviceCheck: 'devices',
	emailNotification: 'notifications',
	emitVirtualButtonEvent: 'variables',
	energyCondition: 'energy',
	eventLogNotification: 'notifications',
	executeOnce: 'time',
	groupAction: 'devices',
	hysteresisCondition: 'devices',
	logicGate: 'flow-logic',
	manualCondition: 'system',
	mathCondition: 'variables',
	norGate: 'flow-logic',
	note: 'system',
	notificationAction: 'notifications',
	presenceCondition: 'presence',
	pushAlarmNotification: 'notifications',
	sceneAction: 'scenes',
	sceneCondition: 'scenes',
	scheduleCondition: 'time',
	scriptAction: 'scripts',
	setVariable: 'variables',
	sunriseSunsetCondition: 'time',
	switchNode: 'flow-logic',
	timeCondition: 'time',
	timer: 'time',
	trigger: 'system',
	variableCondition: 'variables',
	variableSwitch: 'variables',
	virtualComponentAction: 'variables',
	virtualComponentCondition: 'variables',
	virtualComponentEvent: 'variables',
	weatherCondition: 'weather',
	webhookTrigger: 'system',
};

function inferDiscovery(
	card: Omit<NodeTypeConfig, 'discovery'> & {
		discovery?: Partial<CardDiscoveryMetadata>;
	},
): CardDiscoveryMetadata {
	const haystack = `${card.type} ${card.paletteKey ?? ''} ${card.label}`.toLowerCase();
	let role: CardDiscoveryRole =
		card.category === 'actions'
			? 'action'
			: card.category === 'logic'
				? 'branch'
				: card.category === 'conditions'
					? 'condition'
					: 'state';
	if (/timer|time|schedule|sunrise|sunset/.test(haystack)) role = 'timing';
	else if (/manual|event|trigger/.test(haystack) && card.category === 'conditions') role = 'trigger';
	else if (/switch|gate|sequence/.test(haystack)) role = 'branch';
	else if (/variable|note/.test(haystack) && card.category === 'specialCards') role = 'state';

	const entityKinds: CardDiscoveryMetadata['entityKinds'] = [];
	if (/device|presence|hysteresis/.test(haystack)) entityKinds.push('device');
	if (/room/.test(haystack)) entityKinds.push('room');
	if (/scene|alarm/.test(haystack)) entityKinds.push('scene');
	if (/group/.test(haystack)) entityKinds.push('group');
	if (/script/.test(haystack)) entityKinds.push('script');
	if (/variable|math/.test(haystack)) entityKinds.push('variable');
	if (/notification|weather/.test(haystack)) entityKinds.push('service');

	const authored = card.discovery ?? {};
	const paletteKey = card.paletteKey ?? card.type;
	return {
		role: authored.role ?? role,
		group: authored.group ?? DISCOVERY_GROUP_BY_PALETTE_KEY[paletteKey] ?? 'system',
		domains: authored.domains?.length ? authored.domains : ['core'],
		intents: authored.intents?.length ? authored.intents : ROLE_INTENTS[authored.role ?? role],
		searchAliases: authored.searchAliases?.length
			? authored.searchAliases
			: [card.label, card.description ?? '', card.type, card.paletteKey ?? ''].filter(Boolean),
		entityKinds: authored.entityKinds ?? entityKinds,
		complexity:
			authored.complexity ??
			(card.category === 'specialCards'
				? 'advanced'
				: card.category === 'logic'
					? 'intermediate'
					: 'basic'),
		highlighted: authored.highlighted ?? false,
		quickFlowSupport: authored.quickFlowSupport ?? (card.type === 'note' ? 'read-only' : 'full'),
		recommendedNext:
			authored.recommendedNext ??
			(role === 'trigger' || role === 'condition'
				? ['condition', 'action', 'branch']
				: role === 'action' || role === 'timing'
					? ['action', 'timing', 'condition']
					: ['action', 'condition']),
	};
}

function withDiscovery(
	card: Omit<NodeTypeConfig, 'discovery'> & {
		discovery?: Partial<CardDiscoveryMetadata>;
	},
): NodeTypeConfig {
	return { ...card, discovery: inferDiscovery(card) };
}

const RAW_NODE_TYPE_CONFIG: Array<
	Omit<NodeTypeConfig, 'discovery'> & {
		discovery?: Partial<CardDiscoveryMetadata>;
	}
> = [
	// ── Legacy ──────────────────────────────────────────────────────────────────
	// Standalone trigger node is deprecated. Kept so old graphs still render.
	{
		type: 'trigger',
		label: 'Trigger',
		icon: 'fa-bolt',
		color: '#eab308',
		category: 'conditions',
		deprecated: true,
	},

	// ── Conditions ───────────────────────────────────────────────────────────────
	// ── Logic ────────────────────────────────────────────────────────────────────
	{
		type: 'switchNode',
		label: 'Switch / Case',
		description: 'Branch into multiple paths based on a device property value',
		icon: 'fa-shuffle',
		color: '#e879f9',
		category: 'logic',
		defaultData: () => ({
			sourceRef: '',
			sourceLabel: '',
			cases: [
				{ id: 'c0', operator: 'eq', value: '', label: 'Case 1' },
				{ id: 'c1', operator: 'eq', value: '', label: 'Case 2' },
			],
		}),
	},
	{
		type: 'logicGate',
		label: 'Any',
		description: 'Continue when any connected condition is true or triggered',
		icon: 'fa-code-branch',
		color: '#10b981',
		category: 'logic',
		defaultData: () => ({ gateType: 'or', conditionCount: 2 }),
	},
	{
		type: 'logicGate',
		paletteKey: 'andGate',
		label: 'And',
		description: 'Continue only when all connected conditions are true',
		icon: 'fa-link',
		color: '#10b981',
		category: 'logic',
		defaultData: () => ({ gateType: 'and', conditionCount: 2 }),
	},
	{
		type: 'logicGate',
		paletteKey: 'norGate',
		label: 'NOR Gate',
		description: 'True when none of the inputs match (inverted OR)',
		icon: 'fa-not-equal',
		color: '#10b981',
		category: 'logic',
		defaultData: () => ({ gateType: 'nor', conditionCount: 2 }),
	},

	// ── Actions ──────────────────────────────────────────────────────────────────
	// ── Utilities ────────────────────────────────────────────────────────────────
	{
		type: 'hysteresisCondition',
		label: 'Hysteresis Condition',
		description: 'Turn on above one threshold and off below another, without rapid switching',
		icon: 'fa-wave-square',
		color: '#14b8a6',
		category: 'specialCards',
		discovery: { role: 'condition' },
		defaultData: () => ({
			deviceId: '',
			deviceName: '',
			channel: '0',
			property: '',
			lowerThreshold: 20,
			upperThreshold: 22,
		}),
	},
	{
		type: 'actionSequence',
		label: 'Action Sequence',
		description: 'Run an ordered list of notifications, variable updates, and delays',
		icon: 'fa-list-ol',
		color: '#8b5cf6',
		category: 'specialCards',
		discovery: { role: 'action' },
		defaultData: () => ({
			steps: [
				{
					id: 'step-1',
					type: 'notification',
					notificationType: 'event_log',
					message: '',
				},
			],
		}),
	},
	{
		type: 'variableSwitch',
		label: 'Variable Switch',
		description: 'Branch into named case paths based on a Flow variable',
		icon: 'fa-code-branch',
		color: '#e879f9',
		category: 'specialCards',
		discovery: { role: 'branch' },
		defaultData: () => ({
			variableName: '',
			cases: [
				{ id: 'c0', operator: 'eq', value: '', label: 'Case 1' },
				{ id: 'c1', operator: 'eq', value: '', label: 'Case 2' },
			],
		}),
	},
	...FLOW_CARD_DEFINITIONS.map((card) => ({
		type: card.type,
		...(card.paletteKey ? { paletteKey: card.paletteKey } : {}),
		label: card.label,
		description: card.description,
		icon: card.icon,
		color: card.color,
		category: card.category,
		premiumOnly: card.premiumOnly,
		deviceRequirement: card.deviceRequirement,
		discovery: { domains: [card.domain], ...card.discovery },
		defaultData: card.defaults,
	})),
	{
		type: 'note',
		label: 'Note',
		description: 'Comment or reminder — not executed; saved only in the flow layout',
		icon: 'fa-note-sticky',
		color: '#f97316',
		category: 'specialCards',
		defaultData: () => ({ noteText: '', noteColor: 'orange' }),
	},
];

export const NODE_TYPE_CONFIG: NodeTypeConfig[] = RAW_NODE_TYPE_CONFIG.map(withDiscovery);

/** Options for Select/dropdown (value, title) — excludes deprecated node types. */
export function getNodeTypeOptions(): { value: NodeType; title: string }[] {
	return NODE_TYPE_CONFIG.filter((n) => !n.deprecated).map((n) => ({
		value: n.type,
		title: n.label,
	}));
}

/** Get config by node type (returns first match). */
export function getNodeTypeConfig(type: string): NodeTypeConfig | undefined {
	return NODE_TYPE_CONFIG.find((n) => n.type === type);
}

/** Get config by paletteKey (falls back to type lookup). */
export function getNodeTypeConfigByPaletteKey(paletteKey: string): NodeTypeConfig | undefined {
	return (
		NODE_TYPE_CONFIG.find((n) => (n.paletteKey ?? n.type) === paletteKey) ??
		NODE_TYPE_CONFIG.find((n) => n.type === paletteKey)
	);
}

/** Group by category for the node palette — excludes deprecated node types. */
export function getNodeTypesByCategory(): Record<NodeCategory, NodeTypeConfig[]> {
	const byCategory: Record<NodeCategory, NodeTypeConfig[]> = {
		conditions: [],
		actions: [],
		specialCards: [],
		logic: [],
	};
	for (const config of NODE_TYPE_CONFIG) {
		if (!config.deprecated)
			byCategory[config.category].push({
				...config,
				readiness: getReadinessForPaletteKey(config.paletteKey ?? config.type),
			});
	}
	return byCategory;
}

/**
 * Build default data for a new node.
 * When a paletteKey is provided the matching palette entry's defaultData factory is used,
 * which allows multiple palette entries for the same node type (e.g. Guard vs Device Condition).
 */
export function buildDefaultNodeData(nodeType: string, paletteKey?: string): Record<string, unknown> {
	const config = paletteKey
		? (getNodeTypeConfigByPaletteKey(paletteKey) ?? getNodeTypeConfig(nodeType))
		: getNodeTypeConfig(nodeType);
	const effectivePaletteKey = paletteKey ?? nodeType;
	const base: Record<string, unknown> = {
		label: config?.label ?? `New ${nodeType}`,
		icon: config?.icon,
		color: config?.color,
		paletteKey: effectivePaletteKey,
	};
	// Use per-entry factory if present
	if (config?.defaultData) {
		return { ...base, ...config.defaultData() };
	}
	// Legacy fallback for types not yet in NODE_TYPE_CONFIG
	if (nodeType === 'trigger') {
		base.executionMode = 'once';
		base.hold_off = 5;
	} else if (nodeType === 'alarmCondition') {
		base.filterType = 'alarm';
	} else if (nodeType === 'alarmAction') {
		base.filterType = 'alarm';
	}
	return base;
}
